/**
 * §76: приглашение друзей.
 *
 * Экран из трёх частей: за что платят, ссылка и что уже получилось.
 * Данных приглашённых здесь нет и быть не может — только числа (§40.5).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Share2 } from 'lucide-react';
import { referralApi } from '../api/referral';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { useTranslation } from 'react-i18next';

export function ReferralPage() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const summary = useQuery({ queryKey: ['referral'], queryFn: referralApi.summary });

  const copy = async () => {
    if (!summary.data) return;
    try {
      await navigator.clipboard.writeText(summary.data.link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер недоступен (нет доверенного контекста) — ссылка видна и выделяется
      // руками, поэтому молча ничего не делаем.
    }
  };

  const share = async () => {
    if (!summary.data) return;
    if (!navigator.share) {
      await copy();
      return;
    }
    try {
      await navigator.share({
        title: 'Alterium',
        text: t('referral.shareText'),
        url: summary.data.link,
      });
    } catch {
      // Пользователь закрыл системное окно — это не ошибка.
    }
  };

  const data = summary.data;

  return (
    <div className="mx-auto w-full max-w-content px-6 py-10">
      <PageHeader
        title={t('referral.title')}
        description={
          data
            ? t('referral.terms', {
                referrer: data.referrerPercent,
                referred: data.referredPercent,
              })
            : t('referral.terms', { referrer: 10, referred: 5 })
        }
      />

      <div className="flex flex-col gap-6 rounded-card border border-line bg-surface p-6">
        <div className="flex flex-col gap-3">
          <span className="text-meta text-content-secondary">{t('referral.yourLink')}</span>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <code className="flex-1 overflow-x-auto rounded-control border border-line bg-surface-secondary px-4 py-3 text-body text-content">
              {data?.link ?? '—'}
            </code>
            <div className="flex gap-2">
              <Button variant="primary" disabled={!data} onClick={() => void copy()}>
                {copied ? (
                  <>
                    <Check className="h-4 w-4" strokeWidth={1.75} />{t('common.copied')}</>
                ) : (
                  <>
                    <Copy className="h-4 w-4" strokeWidth={1.75} />{t('common.copy')}</>
                )}
              </Button>
              <Button variant="tertiary" disabled={!data} onClick={() => void share()}>
                <Share2 className="h-4 w-4" strokeWidth={1.75} />{t('common.share')}</Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-control border border-line bg-line sm:grid-cols-3">
          <Stat label={t('referral.invited')} value={data ? String(data.invited) : '—'} />
          <Stat label={t('referral.paid')} value={data ? String(data.paid) : '—'} />
          <Stat
            label={t('referral.earned')}
            value={
              data ? t('billing.unitsPlural', { count: data.earnedUnits }) : '—'
            }
          />
        </div>

        {/* Условия §40.3 — до того, как человек начнёт ждать начисления. */}
        <p className="text-meta text-content-muted">{t('referral.conditions')}</p>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 bg-surface px-4 py-4">
      <span className="text-meta text-content-muted">{label}</span>
      <span className="text-card-title text-content">{value}</span>
    </div>
  );
}
